import React from 'react';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { graphql, createRefetchContainer } from 'react-relay';

// components
import { PdpRespContainer } from './PdpRespContainer/PdpRespContainer';
import { PdpRespContainerNotListed } from './PdpRespContainer/PdpRespContainerNotListed';
import { PdpSharedLayout } from './PdpSharedLayout';

// helpers
import get from 'lodash.get';

// styles
import './styles/PdpRespLayout.scss';

export class PdpRespLayoutComponent extends React.Component {
    componentDidMount() {
        if (this.props.isPdpRespPreviewPage) {
            document.body.classList.add('disable-click-on-preview');
        }
    }

    getAdditionalTrackingData() {
        const additional = {};

        return additional;
    }

    render() {
        const { relayVariables } = this.props;
        const viewer = get(this.props, 'viewer', null);
        const user = get(viewer, 'user', null);
        const item = get(viewer, 'item', null);
        const isListingNotListed = item.isListingNotListed && !get(item, 'seller.isInternal');
        const sharedProps = {
            relayVariables,
            viewer,
            user,
            item,
            similarGridItems: get(item, 'similarGridItems', null),
        };

        return (
            <PdpSharedLayout
                canRunPdpTileTest={relayVariables.showSimilarItems}
                user={user}
                viewer={viewer}
                additionalTrackingData={this.getAdditionalTrackingData()}
                doRefetch={this.props.relay.refetch}
            >
                {({ pdpHead, currency }) => (
                    <React.Fragment>
                        <Helmet {...pdpHead.getPdpData()} />
                        {isListingNotListed && !this.props.isPdpRespPreviewPage ? (
                            // Unlisted Item Pdp
                            <PdpRespContainerNotListed {...sharedProps} currency={currency} />
                        ) : (
                            // Default Pdp
                            <PdpRespContainer
                                {...sharedProps}
                                currency={currency}
                                displayInfo={get(viewer, 'item.displayInfo', null)}
                                sellerItems={get(viewer, 'item.sellerItems', null)}
                                similarLandingItems={get(viewer, 'item.similarLandingItems', null)}
                                vipUserRelationship={get(viewer, 'vipUserRelationships[0]', null)}
                                shipmentQuote={get(
                                    viewer,
                                    'relevantShippingInfo.shipmentQuote',
                                    null
                                )}
                                shipment={get(viewer, 'relevantShippingInfo.shipment', null)}
                                transactions={get(viewer, 'transactions', null)}
                            />
                        )}
                    </React.Fragment>
                )}
            </PdpSharedLayout>
        );
    }
}

PdpRespLayoutComponent.propTypes = {
    relayVariables: PropTypes.shape({
        userId: PropTypes.string,
        userIds: PropTypes.arrayOf(PropTypes.string),
        itemId: PropTypes.string.isRequired,
        itemIds: PropTypes.arrayOf(PropTypes.string).isRequired,
        newAndCustomSplitTest: PropTypes.bool,
        filterOutLessSellableItemsTest: PropTypes.bool,
        isClient: PropTypes.bool.isRequired,
        isMultiSku: PropTypes.bool.isRequired,
        fetchUser: PropTypes.bool.isRequired,
        relationshipUserIds: PropTypes.array.isRequired,
        followSearchPages: PropTypes.array,
        fetchFollowSearch: PropTypes.bool,
        showSimilarItems: PropTypes.bool,
    }).isRequired,
    viewer: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,

    // from redux
    isPdpRespPreviewPage: PropTypes.bool,
    showSimilarItemsTop: PropTypes.func,
};

// Redux Map Functions
function mapStateToProps({ pdpPreviewPage, relayVariables }) {
    return {
        isPdpRespPreviewPage: pdpPreviewPage.isPdpRespPreviewPage,
        relayVariables: relayVariables.variables,
    };
}

export const PdpRespLayout = createRefetchContainer(
    connect(mapStateToProps)(PdpRespLayoutComponent),
    {
        viewer: graphql`
            fragment PdpRespLayout_viewer on Viewer {
                ...PdpRespContainer_viewer
                ...PdpRespContainerNotListed_viewer
                ...PdpSharedLayout_viewer
                user(userId: $userId) @include(if: $fetchUser) {
                    ...PdpRespContainer_user
                    ...PdpRespContainerNotListed_user
                    ...PdpSharedLayout_user
                }
                item(itemId: $itemId) {
                    ...PdpRespContainer_item
                    ...PdpRespContainerNotListed_item
                    isListingNotListed
                    seller {
                        isInternal
                    }
                    displayInfo(page: pdp, buyerId: $userId) @include(if: $isClient) {
                        ...PdpRespContainer_displayInfo
                    }
                    ... @include(if: $fetchPdp) {
                        sellerItems: moreLikeThis(first: 4, seller: INCLUDE) {
                            ...PdpRespContainer_sellerItems
                        }
                    }
                    ... @include(if: $isClient) {
                        similarLandingItems: moreLikeThis(
                            first: 12
                            filterOutLessSellableItems: $filterOutLessSellableItemsTest
                        ) {
                            ...PdpRespContainer_similarLandingItems
                        }
                        similarGridItems: moreLikeThis(
                            first: 8
                            seller: EXCLUDE
                            filterOutLessSellableItems: $filterOutLessSellableItemsTest
                        ) {
                            ...PdpRespContainer_similarGridItems
                            ...PdpRespContainerNotListed_similarGridItems
                        }
                    }
                }
                vipUserRelationships: userRelationships(
                    userIds: $relationshipUserIds
                    relationshipType: VIP
                ) @include(if: $fetchUser) {
                    ...PdpRespContainer_vipUserRelationship
                }
                relevantShippingInfo(itemId: $itemId, buyerId: $userId) @include(if: $isClient) {
                    shipmentQuote {
                        ...PdpRespContainer_shipmentQuote
                    }
                    shipment {
                        ...PdpRespContainer_shipment
                    }
                }
                transactions(itemId: $itemIds, buyerId: $userIds) {
                    ...PdpRespContainer_transactions
                }
            }
        `,
    },
    graphql`
        query PdpRespLayoutRefetchQuery(
            $filterOutLessSellableItemsTest: Boolean
            $isClient: Boolean!
            $itemId: String!
            $itemIds: [String]!
            $userId: String = ""
            $userIds: [String]
            $fetchUser: Boolean!
            $relationshipUserIds: [String]!
            $followSearchPages: [String]
            $fetchFollowSearch: Boolean!
            $isTrade: Boolean!
            $isMultiSku: Boolean!
            $fetchPdp: Boolean!
        ) {
            viewer {
                ...PdpRespLayout_viewer
            }
        }
    `
);
